import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/LanguageContext';
import LanguageToggle from './LanguageToggle';
import LegalDocument from './LegalDocument';
import legalService, { documentsMatch } from '../services/legalService';
import { accountId, captureSession, isSessionCurrent } from '../utils/authSession';
import documents from '../data/legalDocuments.json';
import './LegalAccessGate.css';

export const STATUS_ERROR =
  'We could not verify your account. Check your connection and try again.';
export const SAVE_UNCERTAIN =
  'We could not confirm whether your acceptance was saved. Check its status before continuing.';
export const SAVE_NOT_CONFIRMED =
  'Your acceptance has not been confirmed. Please review both documents and try again.';
export const DOCUMENTS_CHANGED =
  'The current documents are not available in this version of the app. Please reload or contact support.';

function SessionGate({ user, updateUser, logout, children }) {
  const t = useT();
  const [phase, setPhase] = useState('checking');
  const [error, setError] = useState('');
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [session] = useState(captureSession);
  const profile = useRef(null);
  const active = useRef(false);
  const busy = useRef(false);
  const controller = useRef(null);
  const isCurrent = useCallback(
    (operation = controller.current) =>
      active.current &&
      operation === controller.current &&
      !operation?.signal.aborted &&
      isSessionCurrent(session),
    [session]
  );

  const applyReceipt = useCallback(
    (receipt, recovering = false, operation = controller.current) => {
      if (!isCurrent(operation)) return;
      if (receipt.accepted || receipt.exempt) {
        // Previously accepted documents are valid forever; current hashes only govern NEW consent.
        setError('');
        setPhase('allowed');
      } else {
        setTermsChecked(false);
        setPrivacyChecked(false);
        setPhase(documentsMatch(receipt) ? 'required' : 'mismatch');
        setError(
          documentsMatch(receipt) ? (recovering ? SAVE_NOT_CONFIRMED : '') : DOCUMENTS_CHANGED
        );
      }
    },
    [isCurrent]
  );

  const check = useCallback(
    async (recovering = false) => {
      if (busy.current || !isCurrent()) return;
      busy.current = true;
      const operation = controller.current;
      setPhase('checking');
      setError('');
      try {
        // Verify the server's role, including admin, before trusting restored user data.
        const verified = await legalService.getProfile(session, operation.signal);
        if (!isCurrent(operation)) return;
        if (
          verified.role === 'admin' &&
          verified.adminWorkspace !== undefined &&
          verified.adminWorkspace !== 'distributor'
        )
          throw new Error(STATUS_ERROR);
        profile.current = verified;
        if (
          verified.role !== user.role ||
          (verified.adminWorkspace ?? null) !== (user.adminWorkspace ?? null) ||
          (verified.role === 'master_distributor' &&
            String(verified.distributor || '') !== String(user.distributor || ''))
        ) {
          updateUser({ ...verified, adminWorkspace: verified.adminWorkspace }, session);
          return; // The newly keyed access boundary starts closed and checks its own receipt.
        }
        if (verified.role === 'master_distributor')
          updateUser(
            { distributorPreviewExpiresAt: verified.distributorPreviewExpiresAt || null },
            session
          );
        const receipt = await legalService.getStatus(verified, session, operation.signal);
        applyReceipt(receipt, recovering, operation);
      } catch {
        if (isCurrent(operation)) {
          setError(recovering ? SAVE_UNCERTAIN : STATUS_ERROR);
          setPhase(recovering ? 'uncertain' : 'error');
        }
      } finally {
        if (operation === controller.current) busy.current = false;
      }
    },
    [applyReceipt, isCurrent, session, updateUser, user.role, user.adminWorkspace, user.distributor]
  );

  useEffect(() => {
    active.current = true;
    controller.current = new AbortController();
    check();
    return () => {
      active.current = false;
      controller.current.abort();
      busy.current = false;
    };
    // One check per account/session/access boundary. Labels and profile-name edits do not reset it.
  }, [check]);

  const accept = async (event) => {
    event.preventDefault();
    if (!isCurrent() || busy.current || phase !== 'required' || !termsChecked || !privacyChecked)
      return;
    busy.current = true; // Synchronous: two callbacks in one frame cannot create two POSTs.
    const operation = controller.current;
    setPhase('saving');
    setError('');
    try {
      const receipt = await legalService.accept(profile.current, session, operation.signal);
      applyReceipt(receipt, false, operation);
    } catch {
      if (!isCurrent(operation)) return;
      // A failed response may follow a committed save. Reconcile, never replay the POST.
      try {
        const receipt = await legalService.getStatus(profile.current, session, operation.signal);
        applyReceipt(receipt, true, operation);
      } catch {
        if (isCurrent(operation)) {
          setPhase('uncertain');
          setError(SAVE_UNCERTAIN);
        }
      }
    } finally {
      if (operation === controller.current) busy.current = false;
    }
  };

  const signOut = () => {
    active.current = false;
    controller.current?.abort();
    logout();
  };

  if (phase === 'allowed' && isSessionCurrent(session)) return children;
  // A cached role cannot establish access. Keep both the private app and consent UI unmounted
  // until the authenticated profile and receipt say which screen this account actually needs.
  if (!['required', 'saving', 'uncertain', 'mismatch'].includes(phase)) {
    return (
      <main className="legal-gate account-access-gate">
        <section className="legal-gate-card account-access-card" aria-label={t('Account access')}>
          <img src="/assets/logo.png" alt="MedEffects" />
          <LanguageToggle tone="light" />
          {error ? (
            <p role="alert" className="legal-gate-error">
              {t(error)}
            </p>
          ) : (
            <p role="status">{t('Checking your account...')}</p>
          )}
          <div className="legal-gate-actions">
            <button type="button" className="legal-gate-signout" onClick={signOut}>
              {t('Sign out')}
            </button>
            {phase === 'error' && (
              <button type="button" className="legal-gate-primary" onClick={() => check()}>
                {t('Check status')}
              </button>
            )}
          </div>
        </section>
      </main>
    );
  }
  const canConsent = phase === 'required';
  return (
    <main className="legal-gate">
      <div className="legal-gate-shell">
        <header className="legal-gate-brand">
          <div>
            <img src="/assets/logo.png" alt="MedEffects" />
            <span>{t('MedEffects Portal')}</span>
          </div>
          <LanguageToggle tone="light" />
        </header>
        <section className="legal-gate-card" aria-labelledby="legal-gate-title">
          <div className="legal-gate-intro">
            <span className="legal-gate-eyebrow">{t('Before you continue')}</span>
            <h1 id="legal-gate-title">{t('Terms & Privacy')}</h1>
            <p>
              {t(
                'Please read and accept both documents to use MedEffects. Your acceptance is saved to your account across devices.'
              )}
            </p>
          </div>
          <div className="legal-gate-documents">
            <details>
              <summary>{t('Read Terms of Use')}</summary>
              <LegalDocument document={documents.terms_of_service} documentKey="terms" />
            </details>
            <details>
              <summary>{t('Read Privacy Policy')}</summary>
              <LegalDocument document={documents.privacy_policy} documentKey="privacy" />
            </details>
          </div>
          <form
            onSubmit={accept}
            className="legal-gate-consent"
            aria-label={t('Document acceptance')}
          >
            <label>
              <input
                type="checkbox"
                checked={termsChecked}
                disabled={!canConsent}
                onChange={(e) => setTermsChecked(e.target.checked)}
              />
              <span>{t('I have read and accept the Terms of Use.')}</span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={privacyChecked}
                disabled={!canConsent}
                onChange={(e) => setPrivacyChecked(e.target.checked)}
              />
              <span>{t('I have read and accept the Privacy Policy.')}</span>
            </label>
            {phase === 'saving' && <p role="status">{t('Saving your acceptance...')}</p>}
            {error && (
              <p role="alert" className="legal-gate-error">
                {t(error)}
              </p>
            )}
            <div className="legal-gate-actions">
              <button type="button" className="legal-gate-signout" onClick={signOut}>
                {t('Sign out')}
              </button>
              {['error', 'uncertain', 'mismatch'].includes(phase) ? (
                <button
                  type="button"
                  className="legal-gate-primary"
                  onClick={() => check(phase === 'uncertain')}
                >
                  {t('Check status')}
                </button>
              ) : (
                <button
                  type="submit"
                  className="legal-gate-primary"
                  disabled={!canConsent || !termsChecked || !privacyChecked}
                >
                  {t('Continue')}
                </button>
              )}
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}

export default function LegalAccessGate({ children }) {
  const { user, sessionVersion, updateUser, logout } = useAuth();
  // Changing identity, login session or access scope synchronously removes protected children.
  const key = JSON.stringify([
    accountId(user),
    user?.role,
    user?.adminWorkspace,
    user?.distributor,
    sessionVersion,
  ]);
  return (
    <SessionGate key={key} user={user || {}} updateUser={updateUser} logout={logout}>
      {children}
    </SessionGate>
  );
}
